// File: src/components/registration/NfpRegistration.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getAuthToken, setAuthToken } from '../../api/client';
import { nfpApi } from '../../services/nfpApi';
import { useNfpReg } from '../../contexts/NfpRegContext';

import Step1Account from './NfpRegistration/Step1Account';
import Step2Entity from './NfpRegistration/Step2Entity';
import Step3NfpLocations from './NfpRegistration/Step3NfpLocations';
import Step4NfpAgreements from './NfpRegistration/Step4NfpAgreements';
import Step5NfpReview from './NfpRegistration/Step5NfpReview';
import ProgressIndicator from '../Registration/ProgressIndicator';
import tealBack from '../../Images/teal_dback.png';
import rypilLo from '../../Images/rypilLo.png';

const STEPS = [
  { key: 'account',    label: 'Account' },
  { key: 'entity',     label: 'Entity' },
  { key: 'location',   label: 'Location' },
  { key: 'agreements', label: 'Agreements' },
  { key: 'review',     label: 'Review' },
];
const KEY_TO_INDEX = Object.fromEntries(STEPS.map((s, i) => [s.key, i]));
const PEACH = '#EBA477';

/* ---------------- helpers ---------------- */
function getSessionUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}
function extractAccount(draft) {
  const a = draft?.account || {};
  return {
    email: (a.email || '').trim(),
    password: (a.password || '').trim(),
    name: (a.name || a.fullName || '').trim(),
    phone: (a.phone || '').trim(),
    role: (a.role || 'org_admin'),
  };
}

function mapUiStructureToEnum(structure) {
  switch ((structure || '').trim()) {
    case 'Independent':             return 'independent';
    case 'Chapter/Affiliate':       return 'chapter_affiliate';
    case 'Umbrella/Parent':         return 'umbrella_parent';
    case '509(a)(3) Supporting Org':return 'supporting_509a3';
    case 'Religious / Faith-Based Organization': return 'religious_gen';
    case 'Education / Health / Government Affiliated': return 'edu_health_gov';
    default:                        return '';
  }
}

function mapEnumToUiStructure(structure) {
  switch ((structure || '').trim()) {
    case 'independent': return 'Independent';
    case 'chapter_affiliate': return 'Chapter/Affiliate';
    case 'umbrella_parent': return 'Umbrella/Parent';
    case 'supporting_509a3': return '509(a)(3) Supporting Org';
    case 'religious_gen': return 'Religious / Faith-Based Organization';
    case 'edu_health_gov': return 'Education / Health / Government Affiliated';
    default: return '';
  }
}

function normalizeTaxClassification(value) {
  switch ((value || '').trim()) {
    case '501(c)(4) Social Welfare':
      return '501(c)(4) Social Welfare Organization';
    case '501(c)(6) Trade/Business Assoc.':
      return '501(c)(6) Trade / Business Association';
    case '170(c)(1) Government Unit':
      return 'Government Unit (170(c)(1))';
    case 'Religious under Group Ruling':
      return 'Not Sure';
    default:
      return value || '';
  }
}

function mapSupporterSnapshotToDraft(snapshot = {}) {
  const emailListSizeMap = {
    '0_250': '0-250',
    '251_1000': '251-1k',
    '1k_5k': '1k-5k',
    '5k_25k': '5k-25k',
    '25k_100k': '25k-100k',
    '100k_plus': '100k+',
    unknown: 'Unknown',
  };
  const activeDonorMap = {
    '0': '0',
    '1_50': '1-50',
    '51_250': '51-250',
    '251_1k': '251-1k',
    '1k_5k': '1k-5k',
    '5k_plus': '5k+',
    unknown: 'Unknown',
  };
  const totalDonationMap = {
    '0': '$0',
    lt_25k: '<$25k',
    '25k_100k': '$25-100k',
    '100k_500k': '$100-500k',
    '500k_2m': '$500k-$2M',
    gt_2m: '$2M+',
    unknown: 'Unknown',
  };
  const volunteerMap = {
    '0': '0',
    '1_25': '1-25',
    '26_100': '26-100',
    '101_500': '101-500',
    '500_plus': '500+',
    unknown: 'Unknown',
  };
  const socialMap = {
    '0_500': '0-500',
    '501_5k': '501-5k',
    '5k_25k': '5k-25k',
    '25k_100k': '25k-100k',
    '100k_plus': '100k+',
    unknown: 'Unknown',
  };
  const crmMap = {
    salesforce_npsp: 'Salesforce NPSP',
    bloomerang: 'Bloomerang',
    neon: 'Neon One',
    donorperfect: 'DonorPerfect',
    kindful: 'Kindful',
    givebutter: 'Givebutter',
    hubspot: 'HubSpot',
    mailchimp: 'Mailchimp',
    other: 'Other',
    none: 'None',
  };

  return {
    asOf: snapshot.asOf ? new Date(snapshot.asOf).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    isEstimate: snapshot.isEstimate ?? true,
    emailListSize: emailListSizeMap[snapshot.emailContactsRange] || '0-250',
    activeDonors: activeDonorMap[snapshot.activeDonorsL12MRange] || '0',
    totalDonations: totalDonationMap[snapshot.totalDonationsL12MRange] || '$0',
    volunteers: volunteerMap[snapshot.volunteersL12MRange] || '0',
    socialReach: socialMap[snapshot.socialReachRange] || '0-500',
    crmTool: crmMap[snapshot.crmProvider] || 'None',
  };
}

function mapProofTypeToRecognitionType(proofType) {
  switch (proofType) {
    case 'lod': return 'irs_determination_letter';
    case 'operating_under_gen': return 'parent_group_exemption';
    case 'government_unit': return 'government_unit_public_entity';
    case 'other_supporting': return 'other_not_sure';
    default: return '';
  }
}

function formatAddressText(address = {}) {
  return [address.street, address.city, address.state, address.zip].filter(Boolean).join(', ');
}

function buildDraftFromRegistration(reg) {
  if (!reg) return {};

  const applicant = reg.applicant || {};
  const entity = {
    legalName: reg.legalName || '',
    publicName: reg.publicNameDraft || '',
    structure: mapEnumToUiStructure(reg.orgStructure),
    hasOwnEin: reg.hasLocalEIN === false ? 'no' : 'yes',
    ein: reg.ein || '',
    parentEin: reg.parent?.ein || '',
    parentOrganizationName: reg.parent?.name || '',
    gen: reg.parent?.gen || '',
    chapterUnitId: reg.parent?.chapterUnitId || '',
    taxClassification: normalizeTaxClassification(reg.taxClassification || ''),
    missionCategories: Array.isArray(reg.missionCategories) ? reg.missionCategories : [],
    recognitionType: reg.statusRecognition?.type || mapProofTypeToRecognitionType(reg.proof?.type),
    isRecognizedByParent: reg.statusRecognition?.isRecognizedByParent || '',
    isCoveredByParentGroupExemption: reg.statusRecognition?.isCoveredByParentGroupExemption || '',
    governmentEntityName: reg.statusRecognition?.governmentEntityName || '',
    governmentDivision: reg.statusRecognition?.governmentDivision || '',
    recognitionNotes: reg.statusRecognition?.notes || '',
    supporterSnapshot: mapSupporterSnapshotToDraft(reg.supporterSnapshot || {}),
  };

  const locations = {
    serviceAreas: Array.isArray(reg.areasServed) ? reg.areasServed : [],
    activeArea: Array.isArray(reg.areasServed) && reg.areasServed.length ? reg.areasServed[0] : '',
    hasPhysicalLocations: !!reg.hasPhysicalLocations,
    locations: Array.isArray(reg.locationDrafts)
      ? reg.locationDrafts.map((loc) => ({
          ...loc,
          addressText: loc.addressText || formatAddressText(loc.address),
        }))
      : [],
    hq: reg.hqDraft
      ? {
          website: reg.hqDraft.website || '',
          address: reg.hqDraft.address || { street: '', city: '', state: '', zip: '', country: 'USA' },
          placeId: reg.hqDraft.placeId || '',
        }
      : undefined,
  };

  return {
    account: {
      fullName: applicant.name || '',
      email: applicant.email || '',
      phone: applicant.phone || '',
      role: applicant.role || 'org_admin',
      isSignatory: applicant.isSignatory !== false,
      inviteSignatory: applicant.signatoryInvite || null,
      partOfNetwork: !!applicant.partOfNetwork,
    },
    entity,
    locations,
    proof: reg.proof || {},
    agreements: reg.agreements || {},
    signatoryInvite: applicant.signatoryInvite || null,
  };
}

function buildDupePayloadFromDraft(draft) {
  const e = draft?.entity || {};
  const orgStructure = mapUiStructureToEnum(e.structure);
  // UI uses hasOwnEin → model/API uses hasLocalEIN
  const hasLocalEIN =
    e.hasOwnEin === true || e.hasOwnEin === 'yes' || e.hasOwnEin === 'true'
      ? true
      : e.hasOwnEin === false || e.hasOwnEin === 'no' || e.hasOwnEin === 'false'
      ? false
      : undefined;

  return {
    legalName: (e.legalName || '').trim(),
    ein: (e.ein || '').replace(/\D/g, ''),
    parentEin: (e.parentEin || '').replace(/\D/g, ''),
    orgStructure,                         // normalized enum
    hasLocalEIN,                          // when provided
    missionCategories: e.missionCategories || [],
  };
}

export default function NfpRegistration() {
  const navigate = useNavigate();
  const { draft, registration, setRegistration, setDraft } = useNfpReg();

  // Initial step: if BE saved currentStep, respect it; else 0
  const [activeIdx, setActiveIdx] = useState(() => {
    const key = registration?.currentStep;
    return key && KEY_TO_INDEX[key] != null ? KEY_TO_INDEX[key] : 0;
  });
  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState('');
  const [inlineErr, setInlineErr] = useState('');
  const [dupeMatches, setDupeMatches] = useState([]);

  const activeKey = STEPS[activeIdx].key;

  /* ---------- bootstrap ---------- */
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const token = getAuthToken();
        if (!token) {
          setRegistration(null);
          setDraft({});
          setActiveIdx(0);
          setBootLoading(false);
          return; // brand-new flow → Step 1
        }

        // Load my most recent open registration (draft/needs_action/under_review)
        const mine = await nfpApi.listMyRegistrations(); // GET /cause-registrations/mine
        let reg = Array.isArray(mine?.registrations) ? mine.registrations[0] : null;

        if (cancelled) return;

        if (reg) {
          setRegistration(reg);
          setDraft(buildDraftFromRegistration(reg));
          const idx =
            reg.currentStep && KEY_TO_INDEX[reg.currentStep] != null
              ? KEY_TO_INDEX[reg.currentStep]
              : 0; // default to Step 1 (Account)
          setActiveIdx(idx);
        } else {
          // no existing doc → start one and go to Step 1
          const started = await nfpApi.startRegistration(); // auth required
          if (cancelled) return;
          if (started?.registration) {
            setRegistration(started.registration);
            setDraft(buildDraftFromRegistration(started.registration));
            setActiveIdx(0);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setAuthToken(null);
          localStorage.removeItem('user');
          setRegistration(null);
          setDraft({});
          setActiveIdx(0);
          setBootError(e?.message || 'Please sign in to resume your registration.');
        }
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    }
    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDraft, setRegistration]);

  useEffect(() => {
    const hasToken = !!getAuthToken();
    if (!hasToken && activeIdx > 0) {
      setActiveIdx(0);
      setRegistration(null);
    }
  }, [activeIdx, setRegistration]);

  /* -------- step index sync to context + backend -------- */
  const setStepIdx = useCallback((updater) => {
    setActiveIdx((prevIdx) => {
      const next = typeof updater === 'function' ? updater(prevIdx) : updater;
      return Math.max(0, Math.min(next, STEPS.length - 1));
    });
  }, []);

  // Persist currentStep to context…
  useEffect(() => {
    const key = STEPS[activeIdx]?.key;
    if (!key) return;
    const regId = registration?._id;
    const hasToken = !!getAuthToken();
    if (!regId || !hasToken) return;
    if (registration?.currentStep === key) return;
    setRegistration((prev) => (prev ? { ...prev, currentStep: key } : prev));
    nfpApi.updateRegistrationMeta(regId, { currentStep: key }).catch(() => {});
  }, [activeIdx, registration?._id, registration?.currentStep, setRegistration]);

  /* -------- Step 1 -> Step 2: enforce your flow -------- */
  const leaveStep1 = useCallback(async () => {
    setInlineErr('');
    const token = getAuthToken();
    const { email, password, name, phone, role } = extractAccount(draft);

    // If already signed in → ensure registration exists, then continue to Step 2
    if (token) {
      const u = getSessionUser();
      if (email && u?.email && u.email.toLowerCase() !== email.toLowerCase()) {
        setInlineErr(`You are signed in as ${u.email}. Sign out to start a new organization with ${email}, or continue as ${u.email}.`);
        return false;
      }
      if (!registration?._id) {
        const started = await nfpApi.startRegistration();
        if (!started?.registration) {
          setInlineErr('Could not start your registration.');
          return false;
        }
        setRegistration(started.registration);
      }
      return true;
    }

    // Not signed in → validate Step 1 fields
    if (!email || !password || !name || !phone) {
      setInlineErr('Please complete Account: name, email, phone, and password are required.');
      return false;
    }

    // Check: does email already exist?
    const exists = await nfpApi.checkEmailExists(email); // { exists: true|false }
    if (exists?.exists) {
      setInlineErr('We already have an account with that email. Please sign in to resume registration.');
      return false;
    }

    // Create pending account and start the draft
    await nfpApi.startPendingRegistration({
      fullName: name,
      email,
      phone,
      password,
      role: role || 'admin',
    });

    const started = await nfpApi.startRegistration();
    if (!started?.registration) {
      setInlineErr('Could not start your registration.');
      return false;
    }
    setRegistration(started.registration);
    return true;
  }, [draft, registration?._id, setRegistration]);

  /* -------- Step 2 -> Step 3: dupe check (normalized payload) -------- */
  const leaveStep2 = useCallback(async () => {
    setInlineErr('');
    setDupeMatches([]);

    const payload = buildDupePayloadFromDraft(draft);
    // If neither EIN nor name, allow forward (Step2 validation should handle basics)
    if (!payload.ein && !payload.legalName) return true;

    try {
      if (typeof nfpApi.checkOrgDuplicate !== 'function') {
        return true;
      }
      const resp = await nfpApi.checkOrgDuplicate(payload); // { matches: [...] }
      const matches = Array.isArray(resp?.matches) ? resp.matches : [];
      if (matches.length > 0) {
        setDupeMatches(matches);
        setInlineErr(
          'We found existing organizations that may duplicate your entry. Please review the list below. If one of these is your organization, do not create a duplicate.'
        );
        return false;
      }
    } catch (err) {
      console.warn('Duplicate check failed; allowing registration to continue.', err);
    }
    return true;
  }, [draft]);

  /* -------- Navigation guards on Next -------- */
  const goNext = useCallback(async () => {
    if (activeKey === 'account') {
      const ok = await leaveStep1();
      if (!ok) return;
    } else if (activeKey === 'entity') {
      const ok = await leaveStep2();
      if (!ok) return;
    }
    setStepIdx((p) => p + 1);
  }, [activeKey, leaveStep1, leaveStep2, setStepIdx]);

  const goBack = useCallback(() => setStepIdx((p) => p - 1), [setStepIdx]);

  const stepComponent = useMemo(() => {
    switch (activeKey) {
      case 'account':
        return <Step1Account onNext={goNext} />;
      case 'entity':
        return <Step2Entity onNext={goNext} onBack={goBack} />;
      case 'location':
        return <Step3NfpLocations onNext={goNext} onBack={goBack} />;
      case 'agreements':
        return <Step4NfpAgreements onNext={goNext} onBack={goBack} />;
      case 'review':
        return <Step5NfpReview onComplete={() => navigate('/nfp')} onBack={goBack} />;
      default:
        return null;
    }
  }, [activeKey, goBack, goNext, navigate]);

  return (
    <div style={styles.fullscreen}>
      <div style={styles.headerWrap}>
        <h1 style={styles.headerTitle}>NOT-FOR-PROFIT REGISTRATION</h1>
        <div style={styles.headerSub}>Complete your nonprofit profile to get started</div>
      </div>

      <div style={styles.progressWrap}>
        <div style={styles.progressCard}>
          <ProgressIndicator
            currentStep={activeIdx + 1}
            totalSteps={STEPS.length}
            steps={STEPS.map((s) => ({
              key: s.key,
              title: s.label,
              description: stepDescriptions[s.key] || '',
            }))}
            onStepClick={(idx) => idx - 1 <= activeIdx && setStepIdx(idx - 1)}
          />
        </div>
      </div>

      {bootError && (
        <div style={styles.errorCard}>
          <div>{bootError}</div>
          <button onClick={() => setBootError('')} style={styles.dismissBtn}>×</button>
        </div>
      )}

      <div style={styles.contentSection}>
        <div style={styles.contentCard}>
          {bootLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={styles.spinner} />
              <p style={{ marginTop: 12 }}>Preparing your registration…</p>
            </div>
          ) : (
            <>
              {inlineErr && <div style={styles.alertError}>{inlineErr}</div>}
              {dupeMatches.length > 0 && (
                <div style={styles.dupeBox}>
                  <div style={styles.dupeTitle}>Possible duplicates</div>
                  <ul style={styles.dupeList}>
                    {dupeMatches.map((m) => (
                      <li key={m._id || m.causeId || m.ein || m.legalName}>
                        <div><b>{m.name || m.legalName}</b></div>
                        {m.ein && <div>EIN: {m.ein}</div>}
                        {m.classification && <div>Scope: {m.classification}</div>}
                        {m.address && <div>{m.address}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={styles.stepWrap} className="nfp-step">
                {stepComponent}
              </div>
            </>
          )}
        </div>
        <img src={rypilLo} alt="rypil mark" style={styles.edgeBadge} />
      </div>

      <style>{`.nfp-step input, .nfp-step textarea {
        width: 100%;
        border-radius: 12px;
        border: 1px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.08);
        color: white;
        padding: 10px 14px;
        box-sizing: border-box;
      }
      .nfp-step select {
        width: 100%;
        border-radius: 12px;
        border: 1px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.08);
        color: white;
        padding: 10px 14px;
        box-sizing: border-box;
      }
      .nfp-step select option,
      .nfp-step select optgroup {
        color: #123642;
        background: #f7fbfc;
      }
      .nfp-step label {
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        display: inline-block;
        margin-bottom: 6px;
        opacity: 0.8;
      }
      .nfp-step button.primary {
        background: ${PEACH};
        border: none;
        color: #1d1d1d;
        font-weight: 600;
        padding: 10px 16px;
        border-radius: 12px;
        cursor: pointer;
      }
      .nfp-step button.secondary {
        background: transparent;
        border: 1px solid rgba(255,255,255,0.4);
        color: white;
        padding: 10px 16px;
        border-radius: 12px;
      }
      @media (forced-colors: active) {
        .nfp-step select {
          forced-color-adjust: auto;
        }
      }`}</style>
    </div>
  );
}

const stepDescriptions = {
  account: 'Create your account & role',
  entity: 'Tell us about the organization',
  location: 'List locations & coverage',
  agreements: 'Upload proof & sign terms',
  review: 'Review & submit for approval',
};

const styles = {
  fullscreen: {
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    width: '100%',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 64,
  },
  headerWrap: { marginTop: 56, textAlign: 'center' },
  headerTitle: { margin: 0, fontSize: 36, fontWeight: 700, letterSpacing: 2 },
  headerSub: { marginTop: 8, fontSize: 18, opacity: 0.9 },
  progressWrap: { width: 'min(93vw, 1100px)', marginTop: 24 },
  progressCard: {
    width: '100%',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 16,
    boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
  },
  errorCard: {
    width: 'min(94vw, 1080px)',
    marginTop: 16,
    background: 'rgba(255, 87, 87, 0.12)',
    border: '1px solid rgba(255, 87, 87, 0.4)',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  dismissBtn: {
    background: 'transparent',
    color: 'white',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
  },
  contentSection: {
    width: 'min(94vw, 1100px)',
    marginTop: 20,
    position: 'relative',
    marginBottom: 120,
  },
  contentCard: {
    width: '100%',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 24,
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    overflow: 'hidden',
  },
  edgeBadge: {
    position: 'absolute',
    left: 0,
    top: 'calc(100% + 14px)',
    height: 56,
    opacity: 0.95,
    pointerEvents: 'none',
    zIndex: 2,
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.35)',
    borderTopColor: PEACH,
    animation: 'spin 1s linear infinite',
    margin: '0 auto',
  },
  stepWrap: {
    minHeight: 320,
    color: 'white',
  },
  alertError: {
    border: '1px solid rgba(248,113,113,0.4)',
    borderRadius: 12,
    padding: '10px 14px',
    color: '#FCA5A5',
    fontSize: 13,
    background: 'rgba(248,113,113,0.12)',
    marginBottom: 12,
  },
  dupeBox: {
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    background: 'rgba(255,255,255,0.06)',
  },
  dupeTitle: { fontWeight: 600, marginBottom: 8, fontSize: 13 },
  dupeList: { margin: 0, paddingLeft: 16, display: 'grid', gap: 6, fontSize: 13 },
};
